import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Space,
  Select,
  Switch,
  Button,
  Tag,
  Typography,
  Badge,
  Card,
  Row,
  Col,
  Descriptions,
  notification,
  Statistic,
  Tooltip,
} from 'antd';
import {
  ReloadOutlined,
  WarningOutlined,
  ClockCircleOutlined,
  InfoCircleOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { getHealthEvents, refreshHealthEvents, ignoreHealthEvent } from '../api/client';
import { accountLabel } from '../utils/accountNames';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// ─── Category display ─────────────────────────────────────────────────────────

const CATEGORY_LABEL = {
  scheduledChange: 'Scheduled Change',
  issue: 'Issue',
  accountNotification: 'Account Notification',
  investigation: 'Investigation',
};

const CATEGORY_COLOR = {
  scheduledChange: 'blue',
  issue: 'red',
  accountNotification: 'gold',
  investigation: 'purple',
};

// ─── Status display ───────────────────────────────────────────────────────────

const STATUS_BADGE = {
  upcoming: 'warning',
  open: 'error',
  ongoing: 'error',
  closed: 'default',
};

const STATUS_LABEL = {
  upcoming: 'Upcoming',
  open: 'Open',
  ongoing: 'Ongoing',
  closed: 'Closed',
};

// ─── Health Events Component ──────────────────────────────────────────────────

const HealthEvents = () => {
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Filter state
  const [statusFilter, setStatusFilter] = useState(null);
  const [serviceFilter, setServiceFilter] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [accountFilter, setAccountFilter] = useState(null);
  const [showIgnored, setShowIgnored] = useState(false);

  // Per-row ignore loading state
  const [ignoringArns, setIgnoringArns] = useState(new Set());

  // Derived filter options from loaded data
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);

  // ── Fetch events ────────────────────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getHealthEvents();
      const items = data.events ?? [];
      setEvents(items);
      setFiltered(items);
      setServices([...new Set(items.map((e) => e.service).filter(Boolean))].sort());
      setCategories([...new Set(items.map((e) => e.event_type_category).filter(Boolean))].sort());
      const allAccountIds = items.flatMap((e) =>
        (e.affected_entities ?? []).map((ent) => ent.account_id).filter(Boolean)
      );
      setAccounts([...new Set(allAccountIds)].sort());
    } catch (err) {
      notification.error({
        message: 'Failed to load health events',
        description: 'Please try again.',
        duration: 0,
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // ── Client-side filtering ───────────────────────────────────────────────────
  useEffect(() => {
    let result = [...events];
    if (statusFilter) result = result.filter((e) => e.status_code === statusFilter);
    if (serviceFilter) result = result.filter((e) => e.service === serviceFilter);
    if (categoryFilter) result = result.filter((e) => e.event_type_category === categoryFilter);
    if (accountFilter)
      result = result.filter((e) =>
        (e.affected_entities ?? []).some((ent) => ent.account_id === accountFilter)
      );
    setFiltered(result);
  }, [events, statusFilter, serviceFilter, categoryFilter, accountFilter]);

  // ── Refresh (re-collect from AWS Health API) ────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshHealthEvents();
      notification.success({
        message: 'Health events refreshed',
        description: 'AWS Health API data has been updated.',
        duration: 4,
      });
      await fetchEvents();
    } catch (err) {
      const status = err.response?.status;
      if (status === 503) {
        notification.warning({
          message: 'Health API unavailable',
          description:
            'AWS Business or Enterprise Support is required for organization-level Health events.',
          duration: 0,
        });
      } else {
        notification.error({
          message: 'Refresh failed',
          description: `Status ${status ?? 'unknown'} — please try again.`,
          duration: 0,
        });
      }
    } finally {
      setRefreshing(false);
    }
  };

  // ── Ignore / unignore ───────────────────────────────────────────────────────
  const handleIgnore = async (eventArn, currentlyIgnored) => {
    const action = currentlyIgnored ? 'unignore' : 'ignore';
    setIgnoringArns((prev) => new Set([...prev, eventArn]));
    try {
      await ignoreHealthEvent(eventArn, action);
      setEvents((prev) =>
        prev.map((e) =>
          e.event_arn === eventArn ? { ...e, ignored: action === 'ignore' } : e
        )
      );
      notification.success({
        message: action === 'ignore' ? 'Event ignored' : 'Event restored',
        description: action === 'ignore'
          ? 'This event will be hidden from the default view.'
          : 'This event is now visible in the default view.',
        duration: 3,
      });
    } catch {
      notification.error({ message: 'Failed to update event', duration: 4 });
    } finally {
      setIgnoringArns((prev) => {
        const s = new Set(prev);
        s.delete(eventArn);
        return s;
      });
    }
  };

  // ── Summary counts ──────────────────────────────────────────────────────────
  const openCount = events.filter((e) => e.status_code === 'open' || e.status_code === 'ongoing').length;
  const upcomingCount = events.filter((e) => e.status_code === 'upcoming').length;
  const scheduledCount = events.filter((e) => e.event_type_category === 'scheduledChange').length;

  // ── Derive displayed rows (respects the ignore toggle) ─────────────────────
  const ignoredCount = filtered.filter((e) => e.ignored).length;
  const displayed = showIgnored ? filtered : filtered.filter((e) => !e.ignored);

  // ── Table columns ───────────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Service',
      dataIndex: 'service',
      key: 'service',
      width: 100,
      sorter: (a, b) => (a.service || '').localeCompare(b.service || ''),
      render: (svc) => <Tag color="orange">{svc || '—'}</Tag>,
    },
    {
      title: 'Event Type',
      dataIndex: 'event_type_code',
      key: 'event_type_code',
      sorter: (a, b) => (a.event_type_code || '').localeCompare(b.event_type_code || ''),
      render: (code) => (
        <Tooltip title={code}>
          <Text style={{ fontSize: 12 }}>
            {code?.replace(/^AWS_[^_]+_/, '').replace(/_/g, ' ') || '—'}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Region',
      dataIndex: 'region',
      key: 'region',
      width: 130,
      sorter: (a, b) => (a.region || '').localeCompare(b.region || ''),
      render: (r) => <Text code style={{ fontSize: 12 }}>{r || '—'}</Text>,
    },
    {
      title: 'Account(s)',
      key: 'accounts',
      width: 180,
      sorter: (a, b) => {
        const aId = (a.affected_entities ?? [])[0]?.account_id ?? '';
        const bId = (b.affected_entities ?? [])[0]?.account_id ?? '';
        return accountLabel(aId).localeCompare(accountLabel(bId));
      },
      render: (_, record) => {
        const ids = [...new Set((record.affected_entities ?? []).map((e) => e.account_id).filter(Boolean))];
        if (!ids.length) return <Text type="secondary">—</Text>;
        return (
          <Space size={2} wrap>
            {ids.map((id) => (
              <Tag key={id} style={{ fontSize: 11, margin: 0 }}>{accountLabel(id)}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status_code',
      key: 'status_code',
      width: 110,
      sorter: (a, b) => (a.status_code || '').localeCompare(b.status_code || ''),
      render: (status) => (
        <Badge
          status={STATUS_BADGE[status] || 'default'}
          text={STATUS_LABEL[status] || status || '—'}
        />
      ),
    },
    {
      title: 'Category',
      dataIndex: 'event_type_category',
      key: 'event_type_category',
      width: 160,
      sorter: (a, b) =>
        (a.event_type_category || '').localeCompare(b.event_type_category || ''),
      render: (cat) => (
        <Tag color={CATEGORY_COLOR[cat] || 'default'}>
          {CATEGORY_LABEL[cat] || cat || '—'}
        </Tag>
      ),
    },
    {
      title: 'Start Time',
      dataIndex: 'start_time',
      key: 'start_time',
      width: 160,
      sorter: (a, b) => (a.start_time || '').localeCompare(b.start_time || ''),
      render: (ts) =>
        ts ? (
          <Text style={{ fontSize: 12 }}>{new Date(ts).toLocaleString()}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Affected Resources',
      key: 'affected',
      width: 150,
      align: 'right',
      sorter: (a, b) =>
        (a.affected_entities?.length || 0) - (b.affected_entities?.length || 0),
      render: (_, record) => {
        const count = record.affected_entities?.length || 0;
        return count > 0 ? (
          <Tag color="red">{count}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        );
      },
    },
    {
      title: '',
      key: 'ignore_action',
      width: 44,
      align: 'center',
      render: (_, record) => (
        <Tooltip title={record.ignored ? 'Restore to view' : 'Ignore this event'}>
          <Button
            type="text"
            size="small"
            loading={ignoringArns.has(record.event_arn)}
            icon={record.ignored
              ? <EyeOutlined style={{ color: '#52c41a' }} />
              : <EyeInvisibleOutlined style={{ color: '#8c8c8c' }} />
            }
            onClick={() => handleIgnore(record.event_arn, record.ignored)}
          />
        </Tooltip>
      ),
    },
  ];

  // ── Expandable row: description + affected entities ─────────────────────────
  const expandedRowRender = (record) => {
    const entities = record.affected_entities ?? [];

    const entityColumns = [
      {
        title: 'Account',
        dataIndex: 'account_id',
        key: 'account_id',
        render: (id) => <Text code style={{ fontSize: 12 }}>{accountLabel(id)}</Text>,
      },
      {
        title: 'Resource',
        dataIndex: 'entity_value',
        key: 'entity_value',
        render: (v) => <Text copyable style={{ fontSize: 12 }}>{v || '—'}</Text>,
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        render: (s) => {
          const color = s === 'IMPAIRED' ? 'red' : s === 'UNIMPAIRED' ? 'green' : 'default';
          return s ? <Tag color={color}>{s}</Tag> : <Text type="secondary">—</Text>;
        },
      },
    ];

    return (
      <div style={{ padding: '8px 24px 16px' }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>

          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Event ARN" span={3}>
              <Text copyable style={{ fontSize: 11, wordBreak: 'break-all' }}>
                {record.event_arn}
              </Text>
            </Descriptions.Item>
            {record.ignored && (
              <Descriptions.Item label="Ignored by" span={3}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {record.ignored_by || 'unknown'}
                  {record.ignored_at
                    ? ` on ${new Date(record.ignored_at).toLocaleString()}`
                    : ''}
                </Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Start Time">
              {record.start_time
                ? new Date(record.start_time).toLocaleString()
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="End Time">
              {record.end_time ? new Date(record.end_time).toLocaleString() : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Last Updated">
              {record.last_updated_time
                ? new Date(record.last_updated_time).toLocaleString()
                : '—'}
            </Descriptions.Item>
          </Descriptions>

          {record.description && (
            <Card size="small" title="Event Description" styles={{ body: { padding: 12 } }}>
              <Paragraph
                style={{ whiteSpace: 'pre-wrap', fontSize: 13, margin: 0 }}
              >
                {record.description}
              </Paragraph>
            </Card>
          )}

          {entities.length > 0 && (
            <Card
              size="small"
              title={`Affected Resources (${entities.length})`}
              styles={{ body: { padding: 0 } }}
            >
              <Table
                dataSource={entities}
                columns={entityColumns}
                rowKey={(r, i) => `${r.account_id}-${r.entity_value}-${i}`}
                size="small"
                pagination={false}
              />
            </Card>
          )}

        </Space>
      </div>
    );
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <Title level={4} style={{ margin: 0 }}>AWS Health Events</Title>
        <Button
          type="primary"
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={handleRefresh}
          disabled={loading}
        >
          Refresh from AWS
        </Button>
      </div>

      {/* Summary cards */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Open Events"
              value={openCount}
              prefix={<WarningOutlined />}
              valueStyle={{ color: openCount > 0 ? '#ff4d4f' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Upcoming Events"
              value={upcomingCount}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: upcomingCount > 0 ? '#fa8c16' : '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Scheduled Changes"
              value={scheduledCount}
              prefix={<InfoCircleOutlined />}
              valueStyle={{ color: scheduledCount > 0 ? '#1890ff' : '#52c41a' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters */}
      <Card size="small" title="Filters">
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Status</Text>
            <Select
              placeholder="All statuses"
              value={statusFilter}
              onChange={setStatusFilter}
              allowClear
              style={{ width: '100%' }}
            >
              <Option value="upcoming">Upcoming</Option>
              <Option value="open">Open</Option>
              <Option value="ongoing">Ongoing</Option>
              <Option value="closed">Closed</Option>
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Service</Text>
            <Select
              placeholder="All services"
              value={serviceFilter}
              onChange={setServiceFilter}
              allowClear
              style={{ width: '100%' }}
            >
              {services.map((s) => (
                <Option key={s} value={s}>{s}</Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Category</Text>
            <Select
              placeholder="All categories"
              value={categoryFilter}
              onChange={setCategoryFilter}
              allowClear
              style={{ width: '100%' }}
            >
              {categories.map((c) => (
                <Option key={c} value={c}>
                  <Tag color={CATEGORY_COLOR[c]} style={{ margin: 0 }}>
                    {CATEGORY_LABEL[c] || c}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={6}>
            <Text style={{ display: 'block', fontSize: 12, marginBottom: 4 }}>Account</Text>
            <Select
              placeholder="All accounts"
              value={accountFilter}
              onChange={setAccountFilter}
              allowClear
              style={{ width: '100%' }}
            >
              {accounts.map((id) => (
                <Option key={id} value={id}>{accountLabel(id)}</Option>
              ))}
            </Select>
          </Col>
        </Row>
      </Card>

      {/* Events table */}
      <style>{`
        .health-event-ignored { opacity: 0.45; }
        .health-event-ignored:hover { opacity: 0.75; }
      `}</style>
      <Card
        title={
          <Space>
            <Text strong>Health Events</Text>
            <Tag>{displayed.length} {displayed.length === 1 ? 'event' : 'events'}</Tag>
            {ignoredCount > 0 && (
              <Space size={6}>
                <Switch
                  size="small"
                  checked={showIgnored}
                  onChange={setShowIgnored}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Show {ignoredCount} ignored
                </Text>
              </Space>
            )}
          </Space>
        }
        styles={{ body: { padding: 0 } }}
      >
        <Table
          dataSource={displayed}
          columns={columns}
          rowKey="event_arn"
          rowClassName={(record) => record.ignored ? 'health-event-ignored' : ''}
          loading={loading}
          expandable={{ expandedRowRender }}
          pagination={{
            pageSize: 20,
            showSizeChanger: false,
            showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} events`,
          }}
          size="small"
          scroll={{ x: 900 }}
          locale={{
            emptyText: loading
              ? 'Loading...'
              : 'No health events found. Click "Refresh from AWS" to fetch the latest events.',
          }}
        />
      </Card>

    </Space>
  );
};

export default HealthEvents;
